// ============================================================================
// ENHANCED AttendanceService - Complete Overhaul
// Features:
// 1. Excel-like data structure with proper columns
// 2. Location normalization (merge similar locations by coordinates)
// 3. Late calculation (10 min grace period after 8:00 AM)
// 4. Night shift support with separate filtering
// 5. Optimized caching with TTLs
// 6. Proper hours calculation
// ============================================================================

import { 
  collection, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  onSnapshot,
  Timestamp,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Unsubscribe,
  Query,
  getFirestore
} from 'firebase/firestore';
import { db } from './firebase';  
import { alternativeSaturdayService } from './alternativeSaturday.service';
import { employeeManagementService } from './employeeManagement.service';

// ============================================================================
// CONSTANTS
// ============================================================================
const DUBAI_TIMEZONE = 'Asia/Dubai';

// Cache TTLs
const CACHE_TTL_EMPLOYEES = 10 * 60 * 1000;      // 10 minutes
const CACHE_TTL_ATTENDANCE = 2 * 60 * 1000;      // 2 minutes  
const CACHE_TTL_STATS = 2 * 60 * 1000;           // 2 minutes
const CACHE_TTL_IMAGES = 30 * 60 * 1000;         // 30 minutes
const CACHE_TTL_LOCATIONS = 5 * 60 * 1000;       // 5 minutes

// Work timing defaults
const DEFAULT_START_TIME = '08:00';
const DEFAULT_END_TIME = '18:00';
const LATE_GRACE_PERIOD_MINUTES = 10;  // 10 minute grace period

const DEFAULT_PAGE_SIZE = 100;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

// Location normalization map - merge similar locations
const LOCATION_NORMALIZATION_MAP: { [key: string]: string } = {
  // Site Office variations
  's001-sit': 'SITE OFFICE',
  's001-site': 'SITE OFFICE',
  'site office': 'SITE OFFICE',
  'siteoffice': 'SITE OFFICE',
  'site-office': 'SITE OFFICE',
  
  // Head Office variations
  'head office': 'HEAD OFFICE',
  'headoffice': 'HEAD OFFICE',
  'head-office': 'HEAD OFFICE',
  'ho': 'HEAD OFFICE',
  'h.o': 'HEAD OFFICE',
  'h.o.': 'HEAD OFFICE',
  
  // Add more mappings as needed
};

// Coordinate-based location grouping (lat, lng tolerance: 0.001 = ~100m)
const COORDINATE_TOLERANCE = 0.001;

const INVALID_LOCATION_VALUES = new Set([
  'unknown', 'unknown location', 'unknown location name',
  'null', 'undefined', 'n/a', 'na', ''
]);

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ============================================================================
// INTERFACES
// ============================================================================
export interface AttendanceRecord {
  id: string;
  serialNo: number;
  employeeName: string;
  uuid?: string;
  employeeId: string;
  employeePin: string;
  date: string;
  checkIn: string;
  checkOut: string;
  totalHours: string;
  decimalHours: number;
  status: 'Present' | 'Absent' | 'Late' | 'On Leave' | 'Early Leave' | 'Incomplete';
  lateMinutes: number;
  lateRemarks: string;
  location: string;
  normalizedLocation: string;
  avatar?: string;
  realImage?: string;
  department?: string;
  designation?: string;
  rawCheckIn?: string;
  overtimeHours?: number;
  overtimeType?: string;
  rawCheckOut?: string;
  noBreak?: boolean;
  noBreakReason?: string;
  noBreakAppliedBy?: string;
  noBreakAppliedAt?: string;
  isNightShift?: boolean;
  shiftStartTime?: string;
  shiftEndTime?: string;
  allocatedWorkTime?: string;
}

export interface ExportOptions {
  date: string;
  includeDesignation?: boolean;
  format?: 'csv' | 'xlsx';
  onProgress?: (progress: number) => void;
}

export interface UnregisteredEmployee {
  employeeNumber: string;
  employeeName: string;
  designation: string;
  department: string;
  email?: string;
  status: 'Unregistered';
}

export interface AbsentEmployee {
  employeePin: string;
  employeeName: string;
  designation: string;
  department: string;
  date: string;
  status: 'Absent';
  lastCheckIn?: string;
}

export interface EmployeeRegistrationStats {
  totalEmployees: number;
  registeredEmployees: number;
  registrationRate: number;
  unregisteredCount: number;
}

export interface EnhancedAttendanceStats extends AttendanceStats {
  employeeRegistrationInfo: EmployeeRegistrationStats;
}

export interface Employee {
  id: string;
  name: string;
  pin: string;
  employeeNumber?: string;
  designation?: string;
  department?: string;
  email?: string;
  avatar?: string;
  image?: string;
  realImageUrl?: string;
  isNightShift?: boolean;
  nightShiftStartTime?: string;
  nightShiftEndTime?: string;
  attendanceDateResetHour?: number;
  startTime?: string;
  endTime?: string;
}

export interface AttendanceFilters {
  startDate?: string;
  endDate?: string;
  employeeId?: string;
  employeePin?: string;
  employeeName?: string;
  status?: string[];
  department?: string;
  location?: string;
  isNightShift?: boolean;
  page?: number;
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  sortOrder?: 'asc' | 'desc';
}


export interface SyncTimeSlot {
  startHour: number;
  endHour: number;
  label: string;
  count: number;
  percentage: number;
  employees: Array<{
    employeePin: string;
    employeeName: string;
    checkIn: string;
    syncedAt: string;
    timeDifference: string; // How long after check-in was it synced
    location: string;
    isOfflineSync: boolean;
  }>;
}

export interface SyncTimeAnalysis {
  date: string;
  totalRecords: number;
  timeSlots: SyncTimeSlot[];
  offlineSyncCount: number;
  offlineSyncPercentage: number;
  customRangeAvailable: boolean;
}

export interface SyncTimeFilters {
  date: string;
  startHour: number;
  endHour: number;
  includeOfflineOnly?: boolean;
}
export interface AttendanceStats {
  totalEmployees: number;
  totalRecords: number;
  present: number;
  late: number;
  absent: number;
  onLeave: number;
  earlyLeave: number;
  incomplete: number;
  checkInCount: number;
  checkOutCount: number;
  checkInRate: number;
  noRestCount: number;
  noRestRate: number;
  nightShiftCount: number;
  dayShiftCount: number;
  totalLateMinutes: number;
  averageLateMinutes: number;
}

export interface LocationStats {
  name: string;
  normalizedName: string;
  count: number;
  percentage: number;
  coordinates?: { lat: number; lng: number };
  employees: AttendanceRecord[];
}

export interface EmployeeMonthlyStats {
  employeePin: string;
  employeeName: string;
  month: string;
  year: number;
  totalWorkingDays: number;
  presentDays: number;
  lateDays: number;
  absentDays: number;
  leaveDays: number;
  earlyLeaveDays: number;
  incompleteDays: number;
  totalHours: number;
  averageHours: number;
  attendanceRate: number;
  totalLateMinutes: number;
}

export interface EmployeeWorkTiming {
  employeePin: string;
  employeeName: string;
  startTime: string;
  endTime: string;
  workTiming: string;
  isNightShift: boolean;
  nightShiftStartTime?: string;
  nightShiftEndTime?: string;
  attendanceDateResetHour?: number;
  restTimingActive?: boolean;
  restTimingStartTime?: string;
  restTimingEndTime?: string;
  restTimingStartDate?: string;
  restTimingEndDate?: string;
}

// Cache entry with typed TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// ============================================================================
// ATTENDANCE SERVICE CLASS
// ============================================================================
class AttendanceService {
  private cache = new Map<string, CacheEntry<any>>();
  private activeListeners = new Set<Unsubscribe>();
  private imageCache = new Map<string, { url: string; timestamp: number }>();
  private locationCoordinatesCache = new Map<string, { lat: number; lng: number }>();

  // ========================================================================
  // CACHE MANAGEMENT
  // ========================================================================
  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < cached.ttl) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  private setCache<T>(key: string, data: T, ttl: number = CACHE_TTL_ATTENDANCE): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  private getCachedImage(pin: string): string | null {
    const cached = this.imageCache.get(pin);
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_IMAGES) {
      return cached.url;
    }
    return null;
  }

  private setCachedImage(pin: string, url: string): void {
    this.imageCache.set(pin, { url, timestamp: Date.now() });
  }

  // ========================================================================
  // LOCATION NORMALIZATION
  // ========================================================================
  normalizeLocationName(location: string): string {
    if (!location || location.trim() === '') return 'Unknown';
    
    const cleaned = location.trim().toLowerCase();
    
    // Check normalization map first
    if (LOCATION_NORMALIZATION_MAP[cleaned]) {
      return LOCATION_NORMALIZATION_MAP[cleaned];
    }
    
    // Check for partial matches
    for (const [key, normalizedName] of Object.entries(LOCATION_NORMALIZATION_MAP)) {
      if (cleaned.includes(key) || key.includes(cleaned)) {
        return normalizedName;
      }
    }
    
    // Extract project code patterns
    const projectPatterns = [
      /^(pj\d{4})/i,
      /^(so\d{3})/i,
      /^(bo\d{3})/i,
      /^(s\d{3})/i,
    ];
    
    for (const pattern of projectPatterns) {
      const match = cleaned.match(pattern);
      if (match) {
        return match[1].toUpperCase();
      }
    }
    
    // Clean up the location name
    let normalized = location.trim()
      .replace(/\s*\(Polygon\s+Boundary\)\s*/gi, '')
      .replace(/\s*polygon\s+boundary\s*/gi, '')
      .replace(/^location\s+/i, '')
      .toUpperCase();
    
    // Remove UUID-like strings
    if (UUID_REGEX.test(normalized)) {
      return 'Unknown';
    }
    
    return normalized || 'Unknown';
  }

  // Group locations by coordinates
  groupLocationsByCoordinates(locations: LocationStats[]): LocationStats[] {
    const grouped: LocationStats[] = [];
    const processed = new Set<string>();
    
    for (const location of locations) {
      if (processed.has(location.name)) continue;
      
      // Find all locations with similar coordinates
      const similarLocations = locations.filter(l => {
        if (processed.has(l.name)) return false;
        if (!location.coordinates || !l.coordinates) {
          return l.normalizedName === location.normalizedName;
        }
        
        const latDiff = Math.abs(location.coordinates.lat - l.coordinates.lat);
        const lngDiff = Math.abs(location.coordinates.lng - l.coordinates.lng);
        
        return latDiff <= COORDINATE_TOLERANCE && lngDiff <= COORDINATE_TOLERANCE;
      });
      
      if (similarLocations.length > 1) {
        // Merge similar locations
        const mergedLocation: LocationStats = {
          name: location.normalizedName,
          normalizedName: location.normalizedName,
          count: similarLocations.reduce((sum, l) => sum + l.count, 0),
          percentage: 0,
          coordinates: location.coordinates,
          employees: similarLocations.flatMap(l => l.employees)
        };
        
        grouped.push(mergedLocation);
        similarLocations.forEach(l => processed.add(l.name));
      } else {
        grouped.push({
          ...location,
          name: location.normalizedName
        });
        processed.add(location.name);
      }
    }
    
    return grouped;
  }

  // ========================================================================
  // EMPLOYEE ID MAPPING
  // ========================================================================
  private mapPinToEmployeeNumber(pin: string): string {
    const cleanPin = pin.replace(/^EMP/i, '');
    return `EMP${cleanPin}`;
  }

  private getEmployeePinFromNumber(employeeNumber: string): string {
    return employeeNumber.replace(/^EMP/i, '');
  }

  private getEmployeeIdentifiers(pin: string): string[] {
    const identifiers = new Set<string>();
    
    identifiers.add(pin);
    const cleanPin = pin.replace(/^EMP/i, '');
    identifiers.add(cleanPin);
    identifiers.add(`EMP${cleanPin}`);
    
    if (/^\d+$/.test(cleanPin)) {
      identifiers.add(cleanPin.padStart(4, '0'));
      identifiers.add(`EMP${cleanPin.padStart(4, '0')}`);
    }
    
    identifiers.add(pin.toLowerCase());
    identifiers.add(pin.toUpperCase());
    identifiers.add(cleanPin.toLowerCase());
    identifiers.add(cleanPin.toUpperCase());
    
    return Array.from(identifiers);
  }

  private findEmployeeByIdentifier(employees: Employee[], searchId: string): Employee | null {
    const identifiers = this.getEmployeeIdentifiers(searchId);
    
    for (const employee of employees) {
      if (identifiers.includes(employee.pin) || 
          identifiers.includes(employee.id) ||
          (employee.employeeNumber && identifiers.includes(employee.employeeNumber))) {
        return employee;
      }
      
      const empIdentifiers = this.getEmployeeIdentifiers(employee.pin);
      if (empIdentifiers.some(id => identifiers.includes(id))) {
        return employee;
      }
    }
    
    return null;
  }

  // ========================================================================
  // TIME & DATE FORMATTING
  // ========================================================================
  private formatDubaiTime(timestamp: any): string {
    if (!timestamp) return '--';
    
    try {
      let date: Date;
      
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return '--';
      }

      if (isNaN(date.getTime())) return '--';

      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: DUBAI_TIMEZONE
      });
    } catch {
      return '--';
    }
  }

  private formatDubaiDate(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-CA', {
        timeZone: DUBAI_TIMEZONE
      });
    } catch {
      return '';
    }
  }

  // ========================================================================
  // LATE CALCULATION (10 min grace period)
  // ========================================================================
  calculateLateMinutes(
    checkInTimestamp: any, 
    shiftStartTime: string = DEFAULT_START_TIME,
    isNightShift: boolean = false
  ): { lateMinutes: number; lateRemarks: string } {
    if (!checkInTimestamp) {
      return { lateMinutes: 0, lateRemarks: '' };
    }

    try {
      let checkInDate: Date;
      
      if (checkInTimestamp instanceof Timestamp) {
        checkInDate = checkInTimestamp.toDate();
      } else if (typeof checkInTimestamp === 'object' && checkInTimestamp.seconds) {
        checkInDate = new Date(checkInTimestamp.seconds * 1000);
      } else if (typeof checkInTimestamp === 'string') {
        checkInDate = new Date(checkInTimestamp);
      } else if (checkInTimestamp instanceof Date) {
        checkInDate = checkInTimestamp;
      } else {
        return { lateMinutes: 0, lateRemarks: '' };
      }

      if (isNaN(checkInDate.getTime())) {
        return { lateMinutes: 0, lateRemarks: '' };
      }

      // Convert to Dubai time
      const dubaiCheckIn = new Date(checkInDate.toLocaleString("en-US", { timeZone: DUBAI_TIMEZONE }));
      
      // Parse shift start time
      const [startHour, startMinute] = shiftStartTime.split(':').map(Number);
      
      // Create expected start time for the day
      const expectedStart = new Date(dubaiCheckIn);
      expectedStart.setHours(startHour, startMinute, 0, 0);
      
      // Add grace period (10 minutes)
      const graceEnd = new Date(expectedStart);
      graceEnd.setMinutes(graceEnd.getMinutes() + LATE_GRACE_PERIOD_MINUTES);
      
      // Calculate late minutes
      if (dubaiCheckIn > graceEnd) {
        const lateMs = dubaiCheckIn.getTime() - expectedStart.getTime();
        const lateMinutes = Math.floor(lateMs / (1000 * 60));
        
        // Format remarks
        let lateRemarks = '';
        if (lateMinutes >= 60) {
          const hours = Math.floor(lateMinutes / 60);
          const mins = lateMinutes % 60;
          lateRemarks = mins > 0 ? `${hours}h ${mins}m late` : `${hours}h late`;
        } else {
          lateRemarks = `${lateMinutes}m late`;
        }
        
        return { lateMinutes, lateRemarks };
      }
      
      return { lateMinutes: 0, lateRemarks: '' };
      
    } catch (error) {
      console.error('Error calculating late minutes:', error);
      return { lateMinutes: 0, lateRemarks: '' };
    }
  }

  // ========================================================================
  // TOTAL HOURS CALCULATION
  // ========================================================================
  calculateTotalHours(checkIn: any, checkOut: any): { formatted: string; decimal: number } {
  if (!checkIn || !checkOut) {
    return { formatted: '0:00', decimal: 0 };
  }

  try {
    let checkInDate: Date;
    let checkOutDate: Date;

    // Parse check-in
    if (checkIn instanceof Timestamp) {
      checkInDate = checkIn.toDate();
    } else if (typeof checkIn === 'string') {
      checkInDate = new Date(checkIn);
    } else if (checkIn instanceof Date) {
      checkInDate = checkIn;
    } else if (checkIn?.seconds) {
      checkInDate = new Date(checkIn.seconds * 1000);
    } else {
      return { formatted: '0:00', decimal: 0 };
    }

    // Parse check-out
    if (checkOut instanceof Timestamp) {
      checkOutDate = checkOut.toDate();
    } else if (typeof checkOut === 'string') {
      checkOutDate = new Date(checkOut);
    } else if (checkOut instanceof Date) {
      checkOutDate = checkOut;
    } else if (checkOut?.seconds) {
      checkOutDate = new Date(checkOut.seconds * 1000);
    } else {
      return { formatted: '0:00', decimal: 0 };
    }

    if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
      return { formatted: '0:00', decimal: 0 };
    }

    let diffMs = checkOutDate.getTime() - checkInDate.getTime();

    // =====================================================================
    // FIX: Handle overnight shifts
    // =====================================================================
    // If diffMs is negative, the check-out crossed midnight.
    // This happens when:
    //   - Employee checks in at 08:33 PM (20:33) on Day 1
    //   - Employee checks out at 06:45 AM (06:45) on Day 2
    //   - But Firestore stores both under the same "date" field
    //   - So checkOutDate appears BEFORE checkInDate on the same calendar day
    //
    // Solution: Add 24 hours to the difference
    // =====================================================================
    if (diffMs < 0) {
      const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
      diffMs += TWENTY_FOUR_HOURS_MS;

      // Safety check: if still negative or exceeds 24 hours, it's genuinely invalid
      if (diffMs < 0 || diffMs > TWENTY_FOUR_HOURS_MS) {
        console.warn('Invalid time difference after overnight adjustment:', {
          checkIn: checkInDate.toISOString(),
          checkOut: checkOutDate.toISOString(),
          adjustedDiffMs: diffMs,
        });
        return { formatted: '0:00', decimal: 0 };
      }
    }

    // Cap at 24 hours max
    const MAX_MS = 24 * 60 * 60 * 1000;
    const cappedDiffMs = Math.min(diffMs, MAX_MS);

    const totalMinutes = Math.round(cappedDiffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const decimal = Number((hours + minutes / 60).toFixed(2));

    return {
      formatted: `${hours}:${minutes.toString().padStart(2, '0')}`,
      decimal,
    };
  } catch (e) {
    console.error('Error calculating hours:', e);
    return { formatted: '0:00', decimal: 0 };
  }
}

  // ========================================================================
  // IMAGE HANDLING
  // ========================================================================
  private formatFirebaseImage(imageData: string): string {
    if (!imageData || imageData.trim() === '') {
      return '/images/humanicon.png';
    }

    if (imageData.startsWith('data:image/')) {
      return imageData;
    }

    if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
      return imageData;
    }

    try {
      const cleanBase64 = imageData.trim();
      
      if (cleanBase64.length > 0 && /^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
        return `data:image/jpeg;base64,${cleanBase64}`;
      } else {
        return '/images/humanicon.png';
      }
    } catch {
      return '/images/humanicon.png';
    }
  }





  async getSyncTimeAnalysis(date: string): Promise<SyncTimeAnalysis> {
    const cacheKey = `sync_time_analysis_${date}`;
    const cached = this.getFromCache<SyncTimeAnalysis>(cacheKey);
    if (cached) return cached;
  
    try {
      // Fetch all attendance records for the date
      const recordsRef = collection(db, 'Attendance_Records', 'PTSEmployees', 'Records');
      const attendanceQuery = query(
        recordsRef,
        where('date', '==', date)
      );
  
      const snapshot = await getDocs(attendanceQuery);
      
      if (snapshot.empty) {
        return {
          date,
          totalRecords: 0,
          timeSlots: [],
          offlineSyncCount: 0,
          offlineSyncPercentage: 0,
          customRangeAvailable: false
        };
      }
  
      // Get employees for mapping
      const employees = await this.getAllEmployees();
      const employeesMap = employees.reduce((acc, emp) => {
        const identifiers = this.getEmployeeIdentifiers(emp.pin);
        identifiers.forEach(id => acc[id] = emp);
        if (emp.employeeNumber) {
          const empNumIdentifiers = this.getEmployeeIdentifiers(emp.employeeNumber);
          empNumIdentifiers.forEach(id => acc[id] = emp);
        }
        acc[emp.id] = emp;
        return acc;
      }, {} as { [key: string]: Employee });
  
      // Define time slots (hourly from 12 AM to 11 PM)
      const hourlySlots: SyncTimeSlot[] = [];
      for (let hour = 0; hour < 24; hour++) {
        const nextHour = hour + 1;
        hourlySlots.push({
          startHour: hour,
          endHour: nextHour,
          label: this.formatTimeSlotLabel(hour, nextHour),
          count: 0,
          percentage: 0,
          employees: []
        });
      }
  
      let offlineSyncCount = 0;
      const totalRecords = snapshot.size;
      let processedRecords = 0;
      let skippedRecords = 0;
  
      // Process each record
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeId = data.employeeId || data.employeePin || data.pin;
        const employee = employeesMap[employeeId];
        
        // Use syncedAt if available, otherwise fall back to checkIn
        const syncedAtValue = data.syncedAt || data.checkIn;
        
        if (!syncedAtValue) {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Missing both syncedAt and checkIn`, {
            hasSyncedAt: !!data.syncedAt,
            hasCheckIn: !!data.checkIn,
            employeeName: data.employeeName
          });
          return;
        }
        
        if (!data.checkIn) {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Missing checkIn`, {
            employeeName: data.employeeName
          });
          return;
        }
  
        // Parse syncedAt timestamp (or checkIn as fallback)
        let syncedAtDate: Date;
        if (typeof syncedAtValue === 'string') {
          syncedAtDate = new Date(syncedAtValue);
        } else if (syncedAtValue instanceof Timestamp) {
          syncedAtDate = syncedAtValue.toDate();
        } else if (syncedAtValue?.seconds) {
          syncedAtDate = new Date(syncedAtValue.seconds * 1000);
        } else {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Invalid syncedAt/checkIn format`, {
            syncedAtValue,
            employeeName: data.employeeName
          });
          return;
        }
  
        if (isNaN(syncedAtDate.getTime())) {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Invalid syncedAt date`, {
            employeeName: data.employeeName
          });
          return;
        }
  
        // Convert to Dubai time
        const dubaiSyncTime = new Date(syncedAtDate.toLocaleString("en-US", { timeZone: 'Asia/Dubai' }));
        const syncHour = dubaiSyncTime.getHours();
  
        // Parse checkIn timestamp
        let checkInDate: Date;
        if (data.checkIn instanceof Timestamp) {
          checkInDate = data.checkIn.toDate();
        } else if (typeof data.checkIn === 'string') {
          checkInDate = new Date(data.checkIn);
        } else if (data.checkIn?.seconds) {
          checkInDate = new Date(data.checkIn.seconds * 1000);
        } else {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Invalid checkIn format`);
          return;
        }
  
        // Calculate time difference between check-in and sync
        const timeDiffMs = syncedAtDate.getTime() - checkInDate.getTime();
        const timeDiffMinutes = Math.floor(timeDiffMs / (1000 * 60));
        
        // Consider it offline sync if synced more than 2 minutes after check-in
        const isOfflineSync = timeDiffMinutes > 2;
        if (isOfflineSync) {
          offlineSyncCount++;
        }
  
        const timeDifference = this.formatTimeDifference(timeDiffMinutes);
  
        // Find the appropriate time slot
        const slot = hourlySlots.find(s => syncHour >= s.startHour && syncHour < s.endHour);
        if (slot) {
          slot.count++;
          processedRecords++;
          slot.employees.push({
            employeePin: employee?.pin || employeeId || 'Unknown',
            employeeName: employee?.name || data.employeeName || 'Unknown',
            checkIn: this.formatDubaiTime(data.checkIn),
            syncedAt: dubaiSyncTime.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }),
            timeDifference,
            location: data.checkInLocationName || data.location || 'Unknown',
            isOfflineSync
          });
        } else {
          skippedRecords++;
          console.warn(`Skipped record ${doc.id}: Could not find slot for hour ${syncHour}`);
        }
      });
  
      console.log(`Sync Time Analysis Summary:`, {
        totalRecords,
        processedRecords,
        skippedRecords,
        verification: processedRecords + skippedRecords === totalRecords ? '✅ Match' : '❌ Mismatch'
      });
  
      // Calculate percentages with better rounding to ensure they sum correctly
      let remainingPercentage = 100;
      const slotsWithCounts = hourlySlots.filter(slot => slot.count > 0);
      
      // Calculate exact percentages first
      const exactPercentages = slotsWithCounts.map(slot => ({
        slot,
        exact: (slot.count / totalRecords) * 100
      }));
      
      // Sort by decimal part (descending) to distribute rounding errors fairly
      exactPercentages.sort((a, b) => {
        const decimalA = a.exact - Math.floor(a.exact);
        const decimalB = b.exact - Math.floor(b.exact);
        return decimalB - decimalA;
      });
      
      // Assign floored percentages first
      exactPercentages.forEach(item => {
        item.slot.percentage = Math.floor(item.exact);
        remainingPercentage -= item.slot.percentage;
      });
      
      // Distribute remaining percentage to slots with highest decimal parts
      for (let i = 0; i < remainingPercentage && i < exactPercentages.length; i++) {
        exactPercentages[i].slot.percentage += 1;
      }
      
      // Sort employees by sync time within each slot
      hourlySlots.forEach(slot => {
        slot.employees.sort((a, b) => a.syncedAt.localeCompare(b.syncedAt));
      });
  
      // Filter out empty slots
      const nonEmptySlots = slotsWithCounts;
  
      const analysis: SyncTimeAnalysis = {
        date,
        totalRecords,
        timeSlots: nonEmptySlots,
        offlineSyncCount,
        offlineSyncPercentage: totalRecords > 0 ? Math.round((offlineSyncCount / totalRecords) * 100) : 0,
        customRangeAvailable: true
      };
  
      this.setCache(cacheKey, analysis, CACHE_TTL_STATS);
      return analysis;
  
    } catch (error) {
      console.error('Error analyzing sync times:', error);
      throw new Error('Failed to analyze sync times');
    }
  }
  
  /**
   * Get sync time data for a custom time range
   */
  async getCustomSyncTimeRange(filters: SyncTimeFilters): Promise<SyncTimeSlot> {
    try {
      const { date, startHour, endHour, includeOfflineOnly = false } = filters;
  
      // Fetch all attendance records for the date
      const recordsRef = collection(db, 'Attendance_Records', 'PTSEmployees', 'Records');
      const attendanceQuery = query(
        recordsRef,
        where('date', '==', date)
      );
  
      const snapshot = await getDocs(attendanceQuery);
      
      if (snapshot.empty) {
        return {
          startHour,
          endHour,
          label: this.formatTimeSlotLabel(startHour, endHour),
          count: 0,
          percentage: 0,
          employees: []
        };
      }
  
      // Get employees for mapping
      const employees = await this.getAllEmployees();
      const employeesMap = employees.reduce((acc, emp) => {
        const identifiers = this.getEmployeeIdentifiers(emp.pin);
        identifiers.forEach(id => acc[id] = emp);
        if (emp.employeeNumber) {
          const empNumIdentifiers = this.getEmployeeIdentifiers(emp.employeeNumber);
          empNumIdentifiers.forEach(id => acc[id] = emp);
        }
        acc[emp.id] = emp;
        return acc;
      }, {} as { [key: string]: Employee });
  
      const customSlot: SyncTimeSlot = {
        startHour,
        endHour,
        label: this.formatTimeSlotLabel(startHour, endHour),
        count: 0,
        percentage: 0,
        employees: []
      };
  
      const totalRecords = snapshot.size;
  
      // Process each record
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeId = data.employeeId || data.employeePin || data.pin;
        const employee = employeesMap[employeeId];
        
        if (!data.syncedAt || !data.checkIn) return;
  
        // Parse syncedAt timestamp
        let syncedAtDate: Date;
        if (typeof data.syncedAt === 'string') {
          syncedAtDate = new Date(data.syncedAt);
        } else if (data.syncedAt instanceof Timestamp) {
          syncedAtDate = data.syncedAt.toDate();
        } else if (data.syncedAt?.seconds) {
          syncedAtDate = new Date(data.syncedAt.seconds * 1000);
        } else {
          return;
        }
  
        if (isNaN(syncedAtDate.getTime())) return;
  
        // Convert to Dubai time
        const dubaiSyncTime = new Date(syncedAtDate.toLocaleString("en-US", { timeZone: 'Asia/Dubai' }));
        const syncHour = dubaiSyncTime.getHours();
  
        // Check if within custom range
        if (syncHour < startHour || syncHour >= endHour) return;
  
        // Parse checkIn timestamp
        let checkInDate: Date;
        if (data.checkIn instanceof Timestamp) {
          checkInDate = data.checkIn.toDate();
        } else if (typeof data.checkIn === 'string') {
          checkInDate = new Date(data.checkIn);
        } else if (data.checkIn?.seconds) {
          checkInDate = new Date(data.checkIn.seconds * 1000);
        } else {
          return;
        }
  
        // Calculate time difference
        const timeDiffMs = syncedAtDate.getTime() - checkInDate.getTime();
        const timeDiffMinutes = Math.floor(timeDiffMs / (1000 * 60));
        const isOfflineSync = timeDiffMinutes > 2;
  
        // Filter by offline only if requested
        if (includeOfflineOnly && !isOfflineSync) return;
  
        const timeDifference = this.formatTimeDifference(timeDiffMinutes);
  
        customSlot.count++;
        customSlot.employees.push({
          employeePin: employee?.pin || employeeId || 'Unknown',
          employeeName: employee?.name || data.employeeName || 'Unknown',
          checkIn: this.formatDubaiTime(data.checkIn),
          syncedAt: dubaiSyncTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          }),
          timeDifference,
          location: data.checkInLocationName || data.location || 'Unknown',
          isOfflineSync
        });
      });
  
      // Calculate percentage
      customSlot.percentage = totalRecords > 0 ? Math.round((customSlot.count / totalRecords) * 100) : 0;
      
      // Sort employees by sync time
      customSlot.employees.sort((a, b) => a.syncedAt.localeCompare(b.syncedAt));
  
      return customSlot;
  
    } catch (error) {
      console.error('Error getting custom sync time range:', error);
      throw new Error('Failed to get custom sync time range');
    }
  }
  
  /**
   * Format time slot label
   */
  private formatTimeSlotLabel(startHour: number, endHour: number): string {
    const formatHour = (hour: number): string => {
      if (hour === 0) return '12 AM';
      if (hour === 12) return '12 PM';
      if (hour < 12) return `${hour} AM`;
      return `${hour - 12} PM`;
    };
  
    return `${formatHour(startHour)} - ${formatHour(endHour)}`;
  }
  
  /**
   * Format time difference in human-readable format
   */
  private formatTimeDifference(minutes: number): string {
    if (minutes < 0) return 'Instant';
    if (minutes === 0) return 'Instant';
    if (minutes < 60) return `${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  }       

  


  // ========================================================================
  // GET ALL RECORDS FOR EXPORT - Fetches ALL records with designations
  // ========================================================================
  async getAllRecordsForExport(options: ExportOptions): Promise<AttendanceRecord[]> {
    const { date, includeDesignation = true, onProgress } = options;
    
    try {
      onProgress?.(5);
      
      // Fetch all employees for mapping
      const employees = await this.getAllEmployees();
      const employeesMap = employees.reduce((acc, emp) => {
        const identifiers = this.getEmployeeIdentifiers(emp.pin);
        identifiers.forEach(id => acc[id] = emp);
        if (emp.employeeNumber) {
          const empNumIdentifiers = this.getEmployeeIdentifiers(emp.employeeNumber);
          empNumIdentifiers.forEach(id => acc[id] = emp);
        }
        acc[emp.id] = emp;
        return acc;
      }, {} as { [key: string]: Employee });
      
      onProgress?.(15);
      
      // Fetch MasterSheet data for designations
      let masterSheetMap = new Map<string, { designation: string; department: string }>();  
      
      if (includeDesignation) {
        try {
          const masterSheetRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
          const msSnapshot = await getDocs(masterSheetRef);
          
          msSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const employeeNumber = doc.id;
            const pin = employeeNumber.replace(/^EMP/i, '');
            
            const empData = {
              designation: data.designation || '',
              department: data.department || ''
            };
            
            // Map by various identifiers
            masterSheetMap.set(pin, empData);
            masterSheetMap.set(pin.toLowerCase(), empData);
            masterSheetMap.set(employeeNumber, empData);
            masterSheetMap.set(employeeNumber.toLowerCase(), empData);
          });
          
          console.log(`✅ MasterSheet loaded: ${msSnapshot.size} employees for export`);
        } catch (err) {
          console.error('Error loading MasterSheet:', err);
        }
      }
      
      onProgress?.(25);
      
      // Fetch ALL attendance records for the date (no pagination limit)
      const recordsRef = collection(db, 'Attendance_Records', 'PTSEmployees', 'Records');
      const attendanceQuery = query(
        recordsRef,
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(attendanceQuery);

      
      onProgress?.(50);
      
      console.log(`📊 Export: Found ${snapshot.size} records for ${date}`);
      
      // Process all records
      const allRecords: AttendanceRecord[] = [];
      let serialNo = 0;
      const totalDocs = snapshot.docs.length;
      
      for (let i = 0; i < totalDocs; i++) {
        const doc = snapshot.docs[i];
        serialNo++;
        
        const record = await this.processAttendanceRecord(
          { id: doc.id, ...doc.data() }, 
          employeesMap, 
          serialNo
        );
        
        // Enhance with designation from MasterSheet
        if (includeDesignation) {
          const employeePin = record.employeePin;
          
          // Try to find designation from MasterSheet
          const lookupKeys = [
            employeePin,
            employeePin.toLowerCase(),
            `EMP${employeePin}`,
            employeePin.replace(/^EMP/i, '')
          ];
          
          for (const key of lookupKeys) {
            const msData = masterSheetMap.get(key);
            if (msData) {
              if (msData.designation && !record.designation) {
                record.designation = msData.designation;
              }
              if (msData.department && !record.department) {
                record.department = msData.department;
              }
              break;
            }
          }
        }
        
        allRecords.push(record);
        
        // Update progress every 10%
        if (i % Math.ceil(totalDocs / 10) === 0) {
          const progress = 50 + Math.round((i / totalDocs) * 45);
          onProgress?.(progress);
        }
      }
      
      // Sort alphabetically by employee name
      allRecords.sort((a, b) => {
        const nameA = (a.employeeName || '').toLowerCase();
        const nameB = (b.employeeName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      // Update serial numbers after sorting
      allRecords.forEach((record, index) => {
        record.serialNo = index + 1;
      });
      
      onProgress?.(100);
      
      console.log(`✅ Export complete: ${allRecords.length} records processed`);
      
      return allRecords;
      
    } catch (error) {
      console.error('Error fetching all records for export:', error);
      throw new Error('Failed to fetch records for export');
    }
  }


  

  // ========================================================================
  // WORK TIMING
  // ========================================================================
  async getEmployeeWorkTiming(employeePin: string): Promise<EmployeeWorkTiming | null> {
    const cacheKey = `work_timing_${employeePin}`;
    const cached = this.getFromCache<EmployeeWorkTiming>(cacheKey);
    if (cached) return cached;

    try {
      const employees = await this.getAllEmployees();
      const employee = this.findEmployeeByIdentifier(employees, employeePin);
      
      if (!employee) return null;

      const employeeDoc = doc(db, 'MasterSheet', 'Employee-Data', 'employees', employee.employeeNumber || `EMP${employee.pin}`);
      const snapshot = await getDoc(employeeDoc);
      
      if (!snapshot.exists()) return null;

      const data = snapshot.data();
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      let isRestTimingActive = false;
      if (data.restTimingStartDate && data.restTimingEndDate) {
        const restStart = new Date(data.restTimingStartDate);
        const restEnd = new Date(data.restTimingEndDate);
        const currentDate = new Date(today);
        
        isRestTimingActive = currentDate >= restStart && currentDate <= restEnd;
      }

      const isNightShift = data.isNightShift === true;

      const result: EmployeeWorkTiming = {
        employeePin,
        employeeName: data.employeeName || employee.name,
        startTime: isRestTimingActive 
          ? (data.restTimingStartTime || data.startTime || DEFAULT_START_TIME) 
          : (data.startTime || DEFAULT_START_TIME),
        endTime: isRestTimingActive 
          ? (data.restTimingEndTime || data.endTime || DEFAULT_END_TIME) 
          : (data.endTime || DEFAULT_END_TIME),
        workTiming: data.workTiming || `${data.startTime || DEFAULT_START_TIME} - ${data.endTime || DEFAULT_END_TIME}`,
        isNightShift,
        nightShiftStartTime: data.nightShiftStartTime,
        nightShiftEndTime: data.nightShiftEndTime,
        attendanceDateResetHour: data.attendanceDateResetHour ? parseInt(data.attendanceDateResetHour) : undefined,
        restTimingActive: isRestTimingActive,
        restTimingStartTime: data.restTimingStartTime,
        restTimingEndTime: data.restTimingEndTime,
        restTimingStartDate: data.restTimingStartDate,
        restTimingEndDate: data.restTimingEndDate
      };

      this.setCache(cacheKey, result, CACHE_TTL_EMPLOYEES);
      return result;
      
    } catch {
      return null;
    }
  }

  // ========================================================================
  // STATUS DETERMINATION
  // ========================================================================
  private async determineStatus(
    record: any, 
    workTiming: EmployeeWorkTiming | null
  ): Promise<{ status: AttendanceRecord['status']; lateMinutes: number; lateRemarks: string }> {
    const isNightShift = workTiming?.isNightShift || false;
    const startTime = isNightShift 
      ? (workTiming?.nightShiftStartTime || '18:00')
      : (workTiming?.startTime || DEFAULT_START_TIME);
    const endTime = isNightShift 
      ? (workTiming?.nightShiftEndTime || '06:00')
      : (workTiming?.endTime || DEFAULT_END_TIME);

    // On Leave check
    if (record.workStatus === 'On Leave' || record.status === 'On Leave') {
      return { status: 'On Leave', lateMinutes: 0, lateRemarks: '' };
    }
  
    // No check-in = Absent
    if (!record.checkIn) {
      return { status: 'Absent', lateMinutes: 0, lateRemarks: '' };
    } 
  
    // ALWAYS calculate late minutes first (this should persist)
    const { lateMinutes, lateRemarks } = this.calculateLateMinutes(
      record.checkIn, 
      startTime,
      isNightShift
    );

    // Incomplete check (has check-in but no check-out)
    if (record.checkIn && !record.checkOut) {
      const now = new Date();
      const checkInDate = record.checkIn instanceof Timestamp ? record.checkIn.toDate() : new Date(record.checkIn);
      
      const today = new Date().toLocaleDateString('en-CA', { timeZone: DUBAI_TIMEZONE });
      const recDate = checkInDate.toLocaleDateString('en-CA', { timeZone: DUBAI_TIMEZONE });
      
      if (recDate === today) {
        const dubaiNow = new Date(now.toLocaleString("en-US", { timeZone: DUBAI_TIMEZONE }));
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        // If past end time + 1 hour, mark as incomplete
        if (dubaiNow.getHours() > endHour + 1 || (dubaiNow.getHours() === endHour + 1 && dubaiNow.getMinutes() > endMinute)) {
          return { 
            status: lateMinutes > 0 ? 'Late' : 'Incomplete',  // Keep Late if was late
            lateMinutes, 
            lateRemarks 
          };
        }
      } else if (recDate < today) {
        // Past day without checkout
        return { 
          status: lateMinutes > 0 ? 'Late' : 'Incomplete',  // Keep Late if was late
          lateMinutes, 
          lateRemarks 
        };
      }
      
      // Still working today - show Late if late, otherwise Present
      return { 
        status: lateMinutes > 0 ? 'Late' : 'Present', 
        lateMinutes, 
        lateRemarks 
      };
    }

    // Has both check-in and check-out
    if (record.checkOut) {
      // If employee was late at check-in, they remain "Late" for the day
      // Late status should persist even after checkout
      if (lateMinutes > 0) {
        return { 
          status: 'Late', 
          lateMinutes, 
          lateRemarks 
        };
      }

      // Check for early leave only if NOT late
      let checkOutDate: Date;
      
      if (record.checkOut instanceof Timestamp) {
        checkOutDate = record.checkOut.toDate();
      } else {
        checkOutDate = new Date(record.checkOut);
      }

      if (!isNaN(checkOutDate.getTime())) {
        const dubaiCheckOut = new Date(checkOutDate.toLocaleString("en-US", { timeZone: DUBAI_TIMEZONE }));
        
        const [endHour, endMinute] = endTime.split(':').map(Number);
        const earlyLeaveThreshold = new Date(dubaiCheckOut);
        earlyLeaveThreshold.setHours(endHour, endMinute, 0, 0);
        
        // Check for early leave (more than 30 min before end time)
        const earlyMs = earlyLeaveThreshold.getTime() - dubaiCheckOut.getTime();
        const earlyMinutes = Math.floor(earlyMs / (1000 * 60));
        
        if (earlyMinutes > 30) {
          return { status: 'Early Leave', lateMinutes: 0, lateRemarks: '' };
        }
      }
    }

    // Default: Present (on time check-in and proper checkout)
    return { 
      status: 'Present', 
      lateMinutes: 0, 
      lateRemarks: '' 
    };
  }




  



// ========================================================================
// 🆕 GET ATTENDANCE FOR MULTIPLE UUIDS (Consolidated History)
// ========================================================================
async getConsolidatedAttendanceForEmployee(
  pin: string,
  startDate: string,
  endDate: string
): Promise<AttendanceRecord[]> {
  try {
    // Get all UUIDs ever associated with this PIN
    const allUUIDs = await employeeManagementService.getAllUUIDsForPin(pin);

    if (allUUIDs.length === 0) {
      console.log(`No UUIDs found for PIN: ${pin}`);
      return [];
    }

    console.log(`Found ${allUUIDs.length} UUID(s) for PIN ${pin}:`, allUUIDs);

    // Build query conditions for each UUID
    const queryConditions = allUUIDs.map(uuid => 
      where('__name__', '>=', uuid)
    );

    const allRecords: AttendanceRecord[] = [];
    
    // Fetch employee info for mapping
    const employees = await this.getAllEmployees();
    const employee = this.findEmployeeByIdentifier(employees, pin);

    // Query attendance records within date range
    const attendanceRef = collection(getFirestore(), 'Attendance_Records/PTSEmployees/Records');
    
    // We need to query for date range first, then filter by UUID
    const dateQuery = query(
      attendanceRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    
    const snapshot = await getDocs(dateQuery);
    
    console.log(`Total records in date range: ${snapshot.size}`);

    // Process each record
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const recordDate = data.date;
      
      // Check if this record belongs to any of our UUIDs
      let belongsToEmployee = false;
      let matchingUUID = '';
      
      for (const uuid of allUUIDs) {
        if (doc.id.startsWith(uuid)) {
          belongsToEmployee = true;
          matchingUUID = uuid;
          break;
        }
      }
      
      if (!belongsToEmployee) continue;
      
      // ✅ FIXED: Get proper check-in/check-out times
      const checkInTimestamp = data.checkIn;
      const checkOutTimestamp = data.checkOut;
      
      // Format times
      const checkIn = checkInTimestamp ? this.formatDubaiTime(checkInTimestamp) : '--';
      const checkOut = checkOutTimestamp ? this.formatDubaiTime(checkOutTimestamp) : '--';
      
      // ✅ FIXED: Calculate total hours properly
      const { formatted: totalHours, decimal: decimalHours } = this.calculateTotalHours(
        checkInTimestamp,
        checkOutTimestamp
      );
      
      // ✅ FIXED: Get work timing for status calculation
      const workTiming = await this.getEmployeeWorkTiming(pin);
      
      // ✅ FIXED: Calculate status properly
      const { status, lateMinutes, lateRemarks } = await this.determineStatus({
        checkIn: checkInTimestamp,
        checkOut: checkOutTimestamp,
        workStatus: data.workStatus,
        status: data.status
      }, workTiming);
      
      // ✅ FIXED: Get location properly
      let location = 'Unknown';
      if (data.checkInLocationName && data.checkInLocationName !== 'unknown') {
        location = data.checkInLocationName;
      } else if (data.location && data.location !== 'unknown') {
        location = data.location;
      } else if (data.checkOutLocationName && data.checkOutLocationName !== 'unknown') {
        location = data.checkOutLocationName;
      }
      
      const normalizedLocation = this.normalizeLocationName(location);
      
      allRecords.push({
        id: doc.id,
        serialNo: data.serialNo || 0,
        employeeName: employee?.name || data.employeeName || 'Unknown',
        uuid: matchingUUID,
        employeeId: pin,
        employeePin: pin,
        date: recordDate || '',
        checkIn: checkIn,
        checkOut: checkOut,
        totalHours: totalHours, // ✅ Now formatted as "HH:MM"
        decimalHours: decimalHours,
        status: status, // ✅ Properly calculated status
        lateMinutes: lateMinutes,
        lateRemarks: lateRemarks,
        location: location, // ✅ Proper location
        normalizedLocation: normalizedLocation,
        avatar: employee?.avatar || '/images/humanicon.png',
        realImage: employee?.realImageUrl || '',
        department: employee?.department || '',
        designation: employee?.designation || '',
        rawCheckIn: checkInTimestamp instanceof Timestamp 
          ? checkInTimestamp.toDate().toISOString() 
          : checkInTimestamp,
        rawCheckOut: checkOutTimestamp instanceof Timestamp 
          ? checkOutTimestamp.toDate().toISOString() 
          : checkOutTimestamp,
        isNightShift: workTiming?.isNightShift || false,
        shiftStartTime: workTiming?.startTime || DEFAULT_START_TIME,
        shiftEndTime: workTiming?.endTime || DEFAULT_END_TIME,
        allocatedWorkTime: workTiming 
          ? `${workTiming.startTime} - ${workTiming.endTime}`
          : `${DEFAULT_START_TIME} - ${DEFAULT_END_TIME}`
      });
    }

    // Sort by date (most recent first)
    allRecords.sort((a, b) => b.date.localeCompare(a.date));

    console.log(`Retrieved ${allRecords.length} attendance records for PIN ${pin}`);
    return allRecords;
    
  } catch (error) {
    console.error('Error getting consolidated attendance:', error);
    return [];
  }
}    



  // ========================================================================
  // LOCATION HANDLING
  // ========================================================================
  private getLocationDisplayName(record: any): { display: string; normalized: string } {
    let rawLocation = 'Unknown';
    
    if (record.checkInLocationName && !INVALID_LOCATION_VALUES.has(record.checkInLocationName.toLowerCase())) {
      rawLocation = record.checkInLocationName;
    } else if (record.locationName && !INVALID_LOCATION_VALUES.has(record.locationName.toLowerCase())) {
      rawLocation = record.locationName;
    } else if (record.checkOutLocationName && !INVALID_LOCATION_VALUES.has(record.checkOutLocationName.toLowerCase())) {
      rawLocation = record.checkOutLocationName;
    } else if (record.location && !INVALID_LOCATION_VALUES.has(record.location.toLowerCase())) {
      rawLocation = record.location;
    }

    const normalized = this.normalizeLocationName(rawLocation);
    
    return {
      display: rawLocation,
      normalized
    };
  }

  // ========================================================================
  // ATTENDANCE RECORD PROCESSING
  // ========================================================================
  private async processAttendanceRecord(
    record: any, 
    employeesMap: { [key: string]: Employee },
    serialNo: number
  ): Promise<AttendanceRecord> {
    const employeeId = record.employeeId || record.employeePin || record.pin;
    
    let employee: Employee | undefined = undefined;
    
    if (employeeId) {
      employee = employeesMap[employeeId];
      
      if (!employee) {
        const employeeNumber = this.mapPinToEmployeeNumber(employeeId);
        employee = employeesMap[employeeNumber];
      }
      
      if (!employee) {
        const identifiers = this.getEmployeeIdentifiers(employeeId);
        for (const id of identifiers) {
          if (employeesMap[id]) {
            employee = employeesMap[id];
            break;
          }
        }
      }
    }
    
    const employeeName = record.employeeName || employee?.name || 'Unknown Employee';
    const employeePin = employee?.pin || employeeId || 'Unknown';
    const department = record.department || employee?.department || '';
    const designation = record.designation || employee?.designation || '';
    
    // Get work timing for this employee
    const workTiming = await this.getEmployeeWorkTiming(employeePin);
    
    const checkInTime = this.formatDubaiTime(record.checkIn);
    const checkOutTime = this.formatDubaiTime(record.checkOut);
    const { formatted: totalHours, decimal: decimalHours } = this.calculateTotalHours(record.checkIn, record.checkOut);
    const formattedDate = this.formatDubaiDate(record.checkIn) || record.date || '';
    
    // Determine status with late calculation
    const { status, lateMinutes, lateRemarks } = await this.determineStatus(record, workTiming);
    
    // Get location
    const { display: displayLocation, normalized: normalizedLocation } = this.getLocationDisplayName(record);
    
    const isNightShift = workTiming?.isNightShift || false;
    const shiftStartTime = isNightShift 
      ? (workTiming?.nightShiftStartTime || '18:00')
      : (workTiming?.startTime || DEFAULT_START_TIME);
    const shiftEndTime = isNightShift 
      ? (workTiming?.nightShiftEndTime || '06:00')
      : (workTiming?.endTime || DEFAULT_END_TIME);
    
    return {
      id: record.id,
      serialNo,
      employeeName,
      employeeId: employeeId || 'Unknown',
      employeePin,
      date: formattedDate,
      checkIn: checkInTime,
      checkOut: checkOutTime,
      totalHours,
      decimalHours,
      status,
      lateMinutes,
      lateRemarks,
      location: displayLocation,
      normalizedLocation,
      department,
      designation,
      realImage: this.formatFirebaseImage(record.employeeImage || ''),
      avatar: '/images/humanicon.png',
      rawCheckIn: record.checkIn instanceof Timestamp ? record.checkIn.toDate().toISOString() : record.checkIn,
      rawCheckOut: record.checkOut instanceof Timestamp ? record.checkOut.toDate().toISOString() : record.checkOut,
      noBreak: record.noBreak === true,
      noBreakReason: record.noBreakReason || '',
      noBreakAppliedBy: record.noBreakAppliedBy || '',
      noBreakAppliedAt: record.noBreakAppliedAt ? this.formatDubaiTime(record.noBreakAppliedAt) : '',
      isNightShift,
      shiftStartTime,
      shiftEndTime,
      allocatedWorkTime: `${shiftStartTime} - ${shiftEndTime}`
    };
  }

  // ========================================================================
  // GET ALL EMPLOYEES
  // ========================================================================
  async getAllEmployees(): Promise<Employee[]> {
    try {
      const cached = this.getFromCache<Employee[]>('all_employees');
      if (cached) return cached;

      const [employeesSnapshot, masterSheetSnapshot] = await Promise.all([
        getDocs(collection(db, 'employees')),
        getDocs(collection(db, 'MasterSheet', 'Employee-Data', 'employees'))
      ]);

      const employees: Employee[] = [];
      const employeeMap = new Map<string, Employee>();

      // Process employees collection
      employeesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const employee: Employee = {
          id: doc.id,
          name: data.name || 'Unknown',
          pin: data.pin || doc.id,
          designation: data.designation || '',
          department: data.department || '',
          email: data.email || '',
          avatar: '/images/humanicon.png',
          realImageUrl: this.formatFirebaseImage(data.image || ''),
          isNightShift: data.isNightShift === true,
          nightShiftStartTime: data.nightShiftStartTime,
          nightShiftEndTime: data.nightShiftEndTime,
          attendanceDateResetHour: data.attendanceDateResetHour ? parseInt(data.attendanceDateResetHour) : undefined,
          startTime: data.startTime || DEFAULT_START_TIME,
          endTime: data.endTime || DEFAULT_END_TIME
        };
        
        const identifiers = this.getEmployeeIdentifiers(employee.pin);
        identifiers.forEach(id => employeeMap.set(id, employee));
        employeeMap.set(employee.id, employee);
      });

      // Process MasterSheet collection
      masterSheetSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeNumber = doc.id;
        const pin = this.getEmployeePinFromNumber(employeeNumber);
        
        const existingIdentifiers = this.getEmployeeIdentifiers(pin);
        let existingEmployee: Employee | undefined;
        
        for (const id of existingIdentifiers) {
          if (employeeMap.has(id)) {
            existingEmployee = employeeMap.get(id);
            break;
          }
        }
        
        if (existingEmployee) {
          // Update existing employee with MasterSheet data
          existingEmployee.employeeNumber = employeeNumber;
          existingEmployee.isNightShift = data.isNightShift === true;
          existingEmployee.nightShiftStartTime = data.nightShiftStartTime;
          existingEmployee.nightShiftEndTime = data.nightShiftEndTime;
          existingEmployee.attendanceDateResetHour = data.attendanceDateResetHour ? parseInt(data.attendanceDateResetHour) : undefined;
          existingEmployee.startTime = data.startTime || DEFAULT_START_TIME;
          existingEmployee.endTime = data.endTime || DEFAULT_END_TIME;
          
          const identifiers = this.getEmployeeIdentifiers(employeeNumber);
          identifiers.forEach(id => employeeMap.set(id, existingEmployee!));
        } else {
          const employee: Employee = {
            id: employeeNumber,
            name: data.name || data.employeeName || 'Unknown',
            pin: pin,
            employeeNumber: employeeNumber,
            designation: data.designation || '',
            department: data.department || '',
            email: data.email || '',
            avatar: '/images/humanicon.png',
            realImageUrl: this.formatFirebaseImage(data.image || ''),
            isNightShift: data.isNightShift === true,
            nightShiftStartTime: data.nightShiftStartTime,
            nightShiftEndTime: data.nightShiftEndTime,
            attendanceDateResetHour: data.attendanceDateResetHour ? parseInt(data.attendanceDateResetHour) : undefined,
            startTime: data.startTime || DEFAULT_START_TIME,
            endTime: data.endTime || DEFAULT_END_TIME
          };
          
          const identifiers = this.getEmployeeIdentifiers(pin);
          identifiers.forEach(id => employeeMap.set(id, employee));
          const empNumIdentifiers = this.getEmployeeIdentifiers(employeeNumber);
          empNumIdentifiers.forEach(id => employeeMap.set(id, employee));
          employeeMap.set(employee.id, employee);
        }
      });

      // Get unique employees
      const uniqueEmployees = new Map<string, Employee>();
      employeeMap.forEach(emp => {
        if (!uniqueEmployees.has(emp.id)) {
          uniqueEmployees.set(emp.id, emp);
        }
      });

      employees.push(...Array.from(uniqueEmployees.values()));

      this.setCache('all_employees', employees, CACHE_TTL_EMPLOYEES);
      
      return employees;
    } catch {
      throw new Error('Failed to fetch employees');
    }
  }

  // ========================================================================
  // GET ATTENDANCE RECORDS
  // ========================================================================
  async getAttendanceRecords(filters: AttendanceFilters = {}): Promise<{
    records: AttendanceRecord[];
    hasMore: boolean;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    totalCount: number;
  }> {
    try {
      const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;
      const sortOrder = filters.sortOrder || 'asc'; // Default to ascending
      
      // Build employees map
      const employees = await this.getAllEmployees();
      const employeesMap = employees.reduce((acc, emp) => {
        const identifiers = this.getEmployeeIdentifiers(emp.pin);
        identifiers.forEach(id => acc[id] = emp);
        
        if (emp.employeeNumber) {
          const empNumIdentifiers = this.getEmployeeIdentifiers(emp.employeeNumber);
          empNumIdentifiers.forEach(id => acc[id] = emp);
        }
        
        acc[emp.id] = emp;
        return acc;
      }, {} as { [key: string]: Employee });

      const recordsRef = collection(db, 'Attendance_Records', 'PTSEmployees', 'Records');

      // Employee-specific search
      if (filters.employeePin || filters.employeeId) {
        const searchPin = filters.employeePin || filters.employeeId;
        
        if (!searchPin) {
          throw new Error('Employee PIN is required');
        }
        
        const targetEmployee = this.findEmployeeByIdentifier(employees, searchPin);
        let allSearchIdentifiers = this.getEmployeeIdentifiers(searchPin);
        
        if (targetEmployee) {
          const empIdentifiers = this.getEmployeeIdentifiers(targetEmployee.pin);
          const empIdIdentifiers = this.getEmployeeIdentifiers(targetEmployee.id);
          
          allSearchIdentifiers = [
            ...allSearchIdentifiers,
            ...empIdentifiers,
            ...empIdIdentifiers
          ];
          
          if (targetEmployee.employeeNumber) {
            const empNumIdentifiers = this.getEmployeeIdentifiers(targetEmployee.employeeNumber);
            allSearchIdentifiers = [...allSearchIdentifiers, ...empNumIdentifiers];
          }
        }
        
        allSearchIdentifiers = Array.from(new Set(allSearchIdentifiers));
        
        // Query for matching records
        const fieldQueries: Query<DocumentData>[] = [];
        const primaryIdentifiers = allSearchIdentifiers.slice(0, 5);
        
        primaryIdentifiers.forEach(id => {
          fieldQueries.push(query(recordsRef, where('employeePin', '==', id), limit(500)));
          fieldQueries.push(query(recordsRef, where('employeeId', '==', id), limit(500)));
        });

        const fieldResults = await Promise.all(
          fieldQueries.map(async (q) => {
            try {
              return await getDocs(q);
            } catch {
              return null;
            }
          })
        );
        
        const allMatchingDocs = new Map();
        
        fieldResults.forEach((snapshot) => {
          if (snapshot) {
            snapshot.docs.forEach(doc => {
              if (!allMatchingDocs.has(doc.id)) {
                allMatchingDocs.set(doc.id, doc);
              }
            });
          }
        });

        if (allMatchingDocs.size === 0) {
          return { records: [], hasMore: false, totalCount: 0 };
        }

        // Process records
        let serialNo = 0;
        let allRecords = await Promise.all(
          Array.from(allMatchingDocs.values()).map(async (doc: any) => {
            serialNo++;
            return await this.processAttendanceRecord({ id: doc.id, ...doc.data() }, employeesMap, serialNo);
          })
        );

        // Filter by date range if provided
        if (filters.startDate && filters.endDate) {
          allRecords = allRecords.filter(r => r.date >= filters.startDate! && r.date <= filters.endDate!);
        } else if (filters.startDate) {
          allRecords = allRecords.filter(r => r.date === filters.startDate);
        }

        // Sort by date and time (ascending by default)
        allRecords.sort((a, b) => {
          const dateCompare = sortOrder === 'asc' 
            ? a.date.localeCompare(b.date) 
            : b.date.localeCompare(a.date);
          if (dateCompare !== 0) return dateCompare;
          
          if (a.checkIn === '--' && b.checkIn !== '--') return 1;
          if (b.checkIn === '--' && a.checkIn !== '--') return -1;
          return sortOrder === 'asc' 
            ? a.checkIn.localeCompare(b.checkIn)
            : b.checkIn.localeCompare(a.checkIn);
        });

        // Update serial numbers after sorting
        allRecords.forEach((record, index) => {
          record.serialNo = index + 1;
        });

        // Pagination
        const startIndex = filters.lastDoc ? 
          (allRecords.findIndex(r => r.id === (filters.lastDoc as any)?.id) + 1) : 0;
        const endIndex = startIndex + pageSize;
        const records = allRecords.slice(startIndex, endIndex);
        const hasMore = endIndex < allRecords.length;
        const lastDoc = records.length > 0 ? { id: records[records.length - 1].id } as any : undefined;

        // Fetch images
        const employeePins = [...new Set(records.map(r => r.employeePin).filter(pin => pin && pin !== 'Unknown'))];
        const imageMap = await this.batchFetchEmployeeImages(employeePins);
        
        const enhancedRecords = records.map(record => ({
          ...record,
          realImage: imageMap.get(record.employeePin) || record.realImage || '/images/humanicon.png'
        }));
        
        return { records: enhancedRecords, hasMore, lastDoc, totalCount: allRecords.length };
      }

      // Date-based queries
      let attendanceQuery: Query<DocumentData>;

      if (filters.startDate && filters.endDate) {
        if (filters.startDate === filters.endDate) {
          attendanceQuery = query(
            recordsRef,
            where('date', '==', filters.startDate),
            limit(pageSize + 1)
          );
        } else {
          attendanceQuery = query(
            recordsRef,
            where('date', '>=', filters.startDate),
            where('date', '<=', filters.endDate),
            limit(pageSize + 1)
          );
        }
      } else if (filters.startDate) {
        attendanceQuery = query(
          recordsRef,
          where('date', '==', filters.startDate),
          limit(pageSize + 1)
        );
      } else {
        const today = new Date().toISOString().split('T')[0];
        attendanceQuery = query(
          recordsRef,
          where('date', '==', today),
          limit(pageSize + 1)
        );
      }

      if (filters.lastDoc) {
        attendanceQuery = query(attendanceQuery, startAfter(filters.lastDoc));
      }

      const snapshot = await getDocs(attendanceQuery);
      const docs = snapshot.docs;

      const hasMore = docs.length > pageSize;
      const recordDocs = hasMore ? docs.slice(0, pageSize) : docs;
      const lastDoc = recordDocs.length > 0 ? recordDocs[recordDocs.length - 1] : undefined;

      // Process records with serial numbers
      let serialNo = 0;
      let records = await Promise.all(
        recordDocs.map(async doc => {
          serialNo++;
          return await this.processAttendanceRecord({ id: doc.id, ...doc.data() }, employeesMap, serialNo);
        })
      );

      // Filter by night shift if specified
      if (filters.isNightShift !== undefined) {
        records = records.filter(r => r.isNightShift === filters.isNightShift);
      }

      // Filter by location if specified
      if (filters.location && filters.location !== '') {
        records = records.filter(record => 
          record.normalizedLocation.toLowerCase().includes(filters.location!.toLowerCase()) ||
          record.location.toLowerCase().includes(filters.location!.toLowerCase())
        );
      }

      // Sort (ascending by default - earliest first)
      records.sort((a, b) => {
        const dateCompare = sortOrder === 'asc' 
          ? a.date.localeCompare(b.date) 
          : b.date.localeCompare(a.date);
        if (dateCompare !== 0) return dateCompare;
        
        if (a.checkIn === '--' && b.checkIn !== '--') return 1;
        if (b.checkIn === '--' && a.checkIn !== '--') return -1;
        return sortOrder === 'asc' 
          ? a.checkIn.localeCompare(b.checkIn)
          : b.checkIn.localeCompare(a.checkIn);
      });

      // Update serial numbers after sorting
      records.forEach((record, index) => {
        record.serialNo = index + 1;
      });

      // Fetch images
      const employeePins = [...new Set(records.map(r => r.employeePin).filter(pin => pin && pin !== 'Unknown'))];
      const imageMap = await this.batchFetchEmployeeImages(employeePins);

      const enhancedRecords = records.map(record => ({
        ...record,
        realImage: imageMap.get(record.employeePin) || record.realImage || '/images/humanicon.png'
      }));

      return { records: enhancedRecords, hasMore, lastDoc, totalCount: records.length };

    } catch (error) {
      console.error('Error fetching attendance records:', error);
      throw new Error('Failed to fetch attendance records');
    }
  }

  // ========================================================================
  // BATCH FETCH IMAGES
  // ========================================================================
  async batchFetchEmployeeImages(employeePins: string[]): Promise<Map<string, string>> {
    const imageMap = new Map<string, string>();
    const pinsToFetch: string[] = [];
    
    // Check cache first
    employeePins.forEach(pin => {
      const cached = this.getCachedImage(pin);
      if (cached) {
        imageMap.set(pin, cached);
      } else {
        pinsToFetch.push(pin);
      }
    });

    if (pinsToFetch.length === 0) {
      return imageMap;
    }
    
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const allEmployeeImages = new Map<string, string>();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.pin && data.image && data.image.trim() !== '') {
          const formattedImage = this.formatFirebaseImage(data.image);
          allEmployeeImages.set(data.pin, formattedImage);
          
          const pinVariations = this.getEmployeeIdentifiers(data.pin);
          pinVariations.forEach(variation => {
            allEmployeeImages.set(variation, formattedImage);
            this.setCachedImage(variation, formattedImage);
          });
        }
      });
      
      pinsToFetch.forEach(pin => {
        const image = allEmployeeImages.get(pin);
        if (image) {
          imageMap.set(pin, image);
        } else {
          const pinVariations = this.getEmployeeIdentifiers(pin);
          for (const variation of pinVariations) {
            const varImage = allEmployeeImages.get(variation);
            if (varImage) {
              imageMap.set(pin, varImage);
              break;
            }
          }
        }
      });
      
    } catch {
      // Silent fail
    }
    
    return imageMap;
  }

  // ========================================================================
  // GET LOCATION STATISTICS
  // ========================================================================
  async getLocationStats(date: string): Promise<LocationStats[]> {
    const cacheKey = `location_stats_${date}`;
    const cached = this.getFromCache<LocationStats[]>(cacheKey);
    if (cached) return cached;

    try {
      const { records } = await this.getAttendanceRecords({
        startDate: date,
        endDate: date,
        pageSize: 2000
      });

      // Group by normalized location
      const locationMap = new Map<string, AttendanceRecord[]>();
      
      records.forEach(record => {
        const normalizedLocation = record.normalizedLocation || 'Unknown';
        if (!locationMap.has(normalizedLocation)) {
          locationMap.set(normalizedLocation, []);
        }
        locationMap.get(normalizedLocation)!.push(record);
      });

      const totalRecords = records.length;
      const locationStats: LocationStats[] = Array.from(locationMap.entries())
        .map(([name, employees]) => ({
          name,
          normalizedName: name,
          count: employees.length,
          percentage: totalRecords > 0 ? Math.round((employees.length / totalRecords) * 100) : 0,
          employees
        }))
        .sort((a, b) => b.count - a.count);

      this.setCache(cacheKey, locationStats, CACHE_TTL_LOCATIONS);
      return locationStats;
      
    } catch (error) {
      console.error('Error fetching location stats:', error);
      return [];
    }
  }

  // ========================================================================
  // GET ATTENDANCE STATS
  // ========================================================================
  async getAttendanceStats(date?: string): Promise<AttendanceStats> {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      const cacheKey = `stats_${targetDate}`;
      
      const cached = this.getFromCache<AttendanceStats>(cacheKey);
      if (cached) return cached;

      const { records } = await this.getAttendanceRecords({
        startDate: targetDate,
        endDate: targetDate,
        pageSize: 2000
      });
      
      const registrationStats = await this.getEmployeeRegistrationStats();

      const totalRecords = records.length;
      
      const present = records.filter(r => r.status === 'Present').length;
      const late = records.filter(r => r.status === 'Late').length;
      const onLeave = records.filter(r => r.status === 'On Leave').length;
      const earlyLeave = records.filter(r => r.status === 'Early Leave').length;
      const incomplete = records.filter(r => r.status === 'Incomplete').length;
      
      const checkInCount = records.filter(r => r.checkIn && r.checkIn !== '--').length;
      const checkOutCount = records.filter(r => r.checkOut && r.checkOut !== '--').length;
      
      const absent = Math.max(0, registrationStats.registeredEmployees - checkInCount);
      
      const noRestCount = records.filter(r => r.noBreak === true).length;
      const noRestRate = registrationStats.registeredEmployees > 0 ? Math.round((noRestCount / registrationStats.registeredEmployees) * 100) : 0;
      
      const checkInRate = registrationStats.registeredEmployees > 0 
        ? Math.round((checkInCount / registrationStats.registeredEmployees) * 100) 
        : 0;

      const nightShiftCount = records.filter(r => r.isNightShift === true).length;
      const dayShiftCount = records.filter(r => r.isNightShift !== true).length;
      
      // Calculate total and average late minutes
      const totalLateMinutes = records.reduce((sum, r) => sum + (r.lateMinutes || 0), 0);
      const lateRecords = records.filter(r => r.lateMinutes > 0);
      const averageLateMinutes = lateRecords.length > 0 ? Math.round(totalLateMinutes / lateRecords.length) : 0;

      const stats: AttendanceStats = {
        totalEmployees: registrationStats.totalEmployees,
        totalRecords,
        present,
        late,
        absent,
        onLeave,
        earlyLeave,
        incomplete,
        checkInCount,
        checkOutCount,
        checkInRate,
        noRestCount,
        noRestRate,
        nightShiftCount,
        dayShiftCount,
        totalLateMinutes,
        averageLateMinutes
      };

      this.setCache(cacheKey, stats, CACHE_TTL_STATS);
      
      return stats;
    } catch {
      throw new Error('Failed to calculate attendance statistics');
    }
  }

  // ========================================================================
  // GET EMPLOYEE REGISTRATION STATS
  // ========================================================================
  async getEmployeeRegistrationStats(): Promise<EmployeeRegistrationStats> {
    try {
      const cacheKey = 'employee_registration_stats';
      const cached = this.getFromCache<EmployeeRegistrationStats>(cacheKey);
      if (cached) return cached;

      const [employeesSnapshot, masterSheetSnapshot] = await Promise.all([
        getDocs(collection(db, 'employees')),
        getDocs(collection(db, 'MasterSheet', 'Employee-Data', 'employees'))
      ]);

      const totalEmployees = masterSheetSnapshot.size;
      
      const masterSheetIds = new Set<string>();
      
      masterSheetSnapshot.docs.forEach(doc => {
        const employeeNumber = doc.id;
        const pin = this.getEmployeePinFromNumber(employeeNumber);
        const identifiers = this.getEmployeeIdentifiers(pin);
        identifiers.forEach(id => masterSheetIds.add(id.toLowerCase()));
        masterSheetIds.add(employeeNumber.toLowerCase());
      });
      
      let registeredEmployees = 0;
      employeesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let isValid = false;
        
        if (data.pin) {
          const identifiers = this.getEmployeeIdentifiers(data.pin);
          isValid = identifiers.some(id => masterSheetIds.has(id.toLowerCase()));
        }
        
        if (!isValid) {
          isValid = masterSheetIds.has(doc.id.toLowerCase());
        }
        
        if (isValid && this.checkEmployeeRegistrationStatus(data)) {
          registeredEmployees++;
        }
      });
      
      const registrationRate = totalEmployees > 0 ? Math.round((registeredEmployees / totalEmployees) * 100) : 0;
      const unregisteredCount = Math.max(0, totalEmployees - registeredEmployees);

      const stats: EmployeeRegistrationStats = {
        totalEmployees,
        registeredEmployees,
        registrationRate,
        unregisteredCount
      };

      this.setCache(cacheKey, stats, CACHE_TTL_EMPLOYEES);
      
      return stats;
    } catch {
      throw new Error('Failed to calculate employee registration statistics');
    }
  }

  private checkEmployeeRegistrationStatus(data: any): boolean {
    const faceRegistered = data.faceRegistered;
    const profileCompleted = data.profileCompleted;
    
    if (faceRegistered === true && profileCompleted === true) {
      return true;
    }
    
    const registrationCompleted = data.registrationCompleted;
    if (registrationCompleted !== undefined) {
      if (registrationCompleted === true) return true;
      if (registrationCompleted === false) return false;
    }
    
    const image = data.image;
    if (image && typeof image === 'string' && image.length > 100) {
      return true;
    }
    
    const enhancedFaceFeatures = data.enhancedFaceFeatures;
    if (enhancedFaceFeatures && 
        typeof enhancedFaceFeatures === 'object' &&
        Object.keys(enhancedFaceFeatures).length > 0) {
      return true;
    }
    
    const faceFeatures = data.faceFeatures;
    if (faceFeatures && 
        typeof faceFeatures === 'object' &&
        Object.keys(faceFeatures).length > 0) {
      return true;
    }
    
    return false;
  }

  // ========================================================================
  // ABSENT EMPLOYEES
  // ========================================================================
  async getAbsentEmployees(date: string): Promise<AbsentEmployee[]> {
    const cacheKey = `absent_${date}`;
    const cached = this.getFromCache<AbsentEmployee[]>(cacheKey);
    if (cached) return cached;

    try {
      const employees = await this.getAllEmployees();
      
      const { records } = await this.getAttendanceRecords({
        startDate: date,
        endDate: date,
        pageSize: 2000
      });
      
      const employeesWithCheckIn = new Set<string>();
      
      records.forEach(record => {
        if (record.checkIn && record.checkIn !== '--' && record.employeePin) {
          const identifiers = this.getEmployeeIdentifiers(record.employeePin);
          identifiers.forEach(id => employeesWithCheckIn.add(id.toLowerCase()));
        }
      });
      
      const absentEmployees: AbsentEmployee[] = [];
      
      employees.forEach(employee => {
        const identifiers = this.getEmployeeIdentifiers(employee.pin);
        const hasCheckedIn = identifiers.some(id => employeesWithCheckIn.has(id.toLowerCase()));
        
        if (!hasCheckedIn) {
          absentEmployees.push({
            employeePin: employee.pin,
            employeeName: employee.name,
            designation: employee.designation || 'Not Specified',
            department: employee.department || 'Not Specified',
            date,
            status: 'Absent',
            lastCheckIn: 'No previous record'
          });
        }
      });
      
      absentEmployees.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      
      this.setCache(cacheKey, absentEmployees, CACHE_TTL_ATTENDANCE);
      return absentEmployees;
      
    } catch {
      throw new Error('Failed to fetch absent employees');
    }
  }

  // ========================================================================
  // UNREGISTERED EMPLOYEES
  // ========================================================================
  async getUnregisteredEmployees(): Promise<UnregisteredEmployee[]> {
    const cacheKey = 'unregistered_employees';
    const cached = this.getFromCache<UnregisteredEmployee[]>(cacheKey);
    if (cached) return cached;

    try {
      const masterSheetRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const masterSheetSnapshot = await getDocs(masterSheetRef);
      
      const employeesRef = collection(db, 'employees');
      const employeesSnapshot = await getDocs(employeesRef);
      
      const registeredEmployeesMap = new Map<string, boolean>();
      
      employeesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        const isActuallyRegistered = this.checkEmployeeRegistrationStatus(data);
        
        if (isActuallyRegistered && data.pin) {
          const identifiers = this.getEmployeeIdentifiers(data.pin);
          identifiers.forEach(id => 
            registeredEmployeesMap.set(id.toLowerCase(), true)
          );
          
          registeredEmployeesMap.set(doc.id.toLowerCase(), true);
          
          if (data.employeeNumber) {
            const empNumIdentifiers = this.getEmployeeIdentifiers(data.employeeNumber);
            empNumIdentifiers.forEach(id => 
              registeredEmployeesMap.set(id.toLowerCase(), true)
            );
          }
        }
      });
      
      const unregisteredEmployees: UnregisteredEmployee[] = [];
      
      masterSheetSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeNumber = doc.id;
        const pin = this.getEmployeePinFromNumber(employeeNumber);
        
        const pinVariations = this.getEmployeeIdentifiers(pin);
        const employeeNumberVariations = this.getEmployeeIdentifiers(employeeNumber);
        const allIdentifiers = [
          ...pinVariations, 
          ...employeeNumberVariations, 
          employeeNumber, 
          pin
        ].map(id => id.toLowerCase());
        
        const isRegistered = allIdentifiers.some(id => 
          registeredEmployeesMap.has(id)
        );
        
        if (!isRegistered) {
          unregisteredEmployees.push({
            employeeNumber,
            employeeName: data.name || data.employeeName || 'Unknown Employee',
            designation: data.designation || 'Not Specified',
            department: data.department || 'Not Specified',
            email: data.email || '',
            status: 'Unregistered'
          });
        }
      });
      
      unregisteredEmployees.sort((a, b) => 
        a.employeeName.localeCompare(b.employeeName)
      );
      
      this.setCache(cacheKey, unregisteredEmployees, CACHE_TTL_EMPLOYEES);
      return unregisteredEmployees;
      
    } catch (error) {
      throw new Error('Failed to fetch unregistered employees');
    }
  }

  // ========================================================================
  // EMPLOYEE MONTHLY STATS
  // ========================================================================
  async getEmployeeMonthlyStats(employeePin: string, year?: number, month?: number): Promise<EmployeeMonthlyStats> {
    try {
      const targetYear = year || new Date().getFullYear();
      const targetMonth = month || new Date().getMonth() + 1;
      
      const startDate = `${targetYear}-${targetMonth.toString().padStart(2, '0')}-01`;
      const endDate = `${targetYear}-${targetMonth.toString().padStart(2, '0')}-31`;
      
      const { records } = await this.getAttendanceRecords({
        employeePin,
        startDate,
        endDate,
        pageSize: 1000
      });

      const employees = await this.getAllEmployees();
      const employee = this.findEmployeeByIdentifier(employees, employeePin);
      const employeeName = employee?.name || 'Unknown Employee';
      
      const totalWorkingDays = records.length;
      const presentDays = records.filter(r => r.status === 'Present').length;
      const lateDays = records.filter(r => r.status === 'Late').length;
      const absentDays = records.filter(r => r.status === 'Absent').length;
      const leaveDays = records.filter(r => r.status === 'On Leave').length;
      const earlyLeaveDays = records.filter(r => r.status === 'Early Leave').length;
      const incompleteDays = records.filter(r => r.status === 'Incomplete').length;
      
      const totalHours = records.reduce((sum, record) => {
        return sum + (record.decimalHours || 0);
      }, 0);
      
      const totalLateMinutes = records.reduce((sum, record) => {
        return sum + (record.lateMinutes || 0);
      }, 0);
      
      const averageHours = totalWorkingDays > 0 ? totalHours / totalWorkingDays : 0;
      const attendanceRate = totalWorkingDays > 0 
        ? Math.round(((presentDays + lateDays + earlyLeaveDays) / totalWorkingDays) * 100) 
        : 0;

      return {
        employeePin,
        employeeName,
        month: new Date(targetYear, targetMonth - 1).toLocaleDateString('en-US', { month: 'long' }),
        year: targetYear,
        totalWorkingDays,
        presentDays,
        lateDays,
        absentDays,
        leaveDays,
        earlyLeaveDays,
        incompleteDays,
        totalHours: Math.round(totalHours * 100) / 100,
        averageHours: Math.round(averageHours * 100) / 100,
        attendanceRate,
        totalLateMinutes
      };
      
    } catch {
      throw new Error('Failed to calculate employee monthly statistics');
    }
  }

  // ========================================================================
  // UNIQUE LOCATIONS
  // ========================================================================
  async getUniqueLocations(date?: string): Promise<string[]> {
    try {
      const locationStats = await this.getLocationStats(date || new Date().toISOString().split('T')[0]);
      return locationStats.map(l => l.normalizedName);
    } catch {
      return [];
    }
  }

  // ========================================================================
  // UTILITY METHODS
  // ========================================================================
  clearCache(): void {
    this.cache.clear();
  }

  clearAllCaches(): void {
    this.cache.clear();
    this.imageCache.clear();
  }

  cleanup(): void {
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
    this.clearAllCaches();
  }

  getDubaiDateForInput(): string {
    return new Date().toLocaleDateString('en-CA', {
      timeZone: DUBAI_TIMEZONE
    });
  }

  // ========================================================================
  // CACHE STATISTICS
  // ========================================================================
  getCacheStats(): {
    mainCacheSize: number;
    imageCacheSize: number;
    cacheKeys: string[];
  } {
    return {
      mainCacheSize: this.cache.size,
      imageCacheSize: this.imageCache.size,
      cacheKeys: Array.from(this.cache.keys())
    };
  }

  // ========================================================================
  // TEST CONNECTION
  // ========================================================================
  async testConnection(): Promise<{ success: boolean; latency: number; message: string }> {
    try {
      const start = Date.now();
      
      // Simple query to test connection
      const testRef = collection(db, 'employees');
      const testQuery = query(testRef, limit(1));
      await getDocs(testQuery);
      
      const latency = Date.now() - start;
      
      return {
        success: true,
        latency,
        message: `Connected successfully in ${latency}ms`
      };
    } catch (error: any) {
      return {
        success: false,
        latency: 0,
        message: error.message || 'Connection failed'
      };
    }
  }

  // ========================================================================
  // REAL-TIME ATTENDANCE LISTENER
  // ========================================================================
  onAttendanceRecords(
    filters: AttendanceFilters,
    onData: (records: AttendanceRecord[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    const recordsRef = collection(db, 'Attendance_Records', 'PTSEmployees', 'Records');
    
    const targetDate = filters.startDate || new Date().toISOString().split('T')[0];
    
    const attendanceQuery = query(
      recordsRef,
      where('date', '==', targetDate),
      orderBy('checkIn', 'desc'),
      limit(filters.pageSize || DEFAULT_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      attendanceQuery,
      async (snapshot) => {
        try {
          const employees = await this.getAllEmployees();
          const employeesMap = employees.reduce((acc, emp) => {
            const identifiers = this.getEmployeeIdentifiers(emp.pin);
            identifiers.forEach(id => acc[id] = emp);
            if (emp.employeeNumber) {
              const empNumIdentifiers = this.getEmployeeIdentifiers(emp.employeeNumber);
              empNumIdentifiers.forEach(id => acc[id] = emp);
            }
            acc[emp.id] = emp;
            return acc;
          }, {} as { [key: string]: Employee });

          let serialNo = 0;
          const records = await Promise.all(
            snapshot.docs.map(async doc => {
              serialNo++;
              return await this.processAttendanceRecord(
                { id: doc.id, ...doc.data() }, 
                employeesMap, 
                serialNo
              );
            })
          );

          // Sort ascending by default
          records.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) return dateCompare;
            return a.checkIn.localeCompare(b.checkIn);
          });

          // Update serial numbers
          records.forEach((record, index) => {
            record.serialNo = index + 1;
          });

          onData(records);
        } catch (error: any) {
          onError(error);
        }
      },
      (error) => {
        onError(error);
      }
    );

    this.activeListeners.add(unsubscribe);
    
    return () => {
      unsubscribe();
      this.activeListeners.delete(unsubscribe);
    };
  }

  // ========================================================================
  // GET DEPARTMENTS
  // ========================================================================
  async getDepartments(): Promise<string[]> {
    const cacheKey = 'departments';
    const cached = this.getFromCache<string[]>(cacheKey);
    if (cached) return cached;

    try {
      const employees = await this.getAllEmployees();
      const departments = new Set<string>();
      
      employees.forEach(emp => {
        if (emp.department && emp.department.trim() !== '' && emp.department !== 'Not Specified') {
          departments.add(emp.department);
        }
      });
      
      const deptArray = Array.from(departments).sort();
      this.setCache(cacheKey, deptArray, CACHE_TTL_EMPLOYEES);
      
      return deptArray;
    } catch {
      return [];
    }
  }

  // ========================================================================
  // GET LOCATIONS
  // ========================================================================
  async getLocations(): Promise<string[]> {
    const cacheKey = 'locations';
    const cached = this.getFromCache<string[]>(cacheKey);
    if (cached) return cached;

    try {
      const today = new Date().toISOString().split('T')[0];
      const locationStats = await this.getLocationStats(today);
      
      const locations = locationStats.map(l => l.normalizedName);
      this.setCache(cacheKey, locations, CACHE_TTL_LOCATIONS);
      
      return locations;
    } catch {
      return [];
    }
  }

  // ========================================================================
  // GET COMBINED ABSENT EMPLOYEES
  // ========================================================================
  async getCombinedAbsentEmployees(date: string): Promise<AbsentEmployee[]> {
    const cacheKey = `combined_absent_${date}`;
    const cached = this.getFromCache<AbsentEmployee[]>(cacheKey);
    if (cached) return cached;

    try {
      // Get all employees from MasterSheet
      const masterSheetRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const masterSheetSnapshot = await getDocs(masterSheetRef);
      
      // Get today's attendance records
      const { records } = await this.getAttendanceRecords({
        startDate: date,
        endDate: date,
        pageSize: 2000
      });
      
      // Build set of employees who checked in
      const employeesWithCheckIn = new Set<string>();
      
      records.forEach(record => {
        if (record.checkIn && record.checkIn !== '--' && record.employeePin) {
          const identifiers = this.getEmployeeIdentifiers(record.employeePin);
          identifiers.forEach(id => employeesWithCheckIn.add(id.toLowerCase()));
        }
      });
      
      // Find absent employees
      const absentEmployees: AbsentEmployee[] = [];
      
      masterSheetSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeNumber = doc.id;
        const pin = this.getEmployeePinFromNumber(employeeNumber);
        
        const identifiers = this.getEmployeeIdentifiers(pin);
        const empNumIdentifiers = this.getEmployeeIdentifiers(employeeNumber);
        const allIdentifiers = [...identifiers, ...empNumIdentifiers];
        
        const hasCheckedIn = allIdentifiers.some(id => 
          employeesWithCheckIn.has(id.toLowerCase())
        );
        
        if (!hasCheckedIn) {
          absentEmployees.push({
            employeePin: pin,
            employeeName: data.name || data.employeeName || 'Unknown Employee',
            designation: data.designation || 'Not Specified',
            department: data.department || 'Not Specified',
            date,
            status: 'Absent',
            lastCheckIn: 'No previous record'
          });
        }
      });
      
      // Sort by name
      absentEmployees.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      
      this.setCache(cacheKey, absentEmployees, CACHE_TTL_ATTENDANCE);
      return absentEmployees;
      
    } catch (error) {
      console.error('Error fetching combined absent employees:', error);
      throw new Error('Failed to fetch absent employees');
    }
  }
}

export const attendanceService = new AttendanceService();